import { Divider } from '@mui/material'
import {
	GET_DETAIL_NOTIFICATION,
	GET_EDIT_CONTENT,
	getContentById,
	getDetailNotification,
	getLocationById,
	postContentReach
} from 'api'
import { Button, Icon, Typography } from 'components'
import { useToast } from 'hooks'
import {
	ContentResponse,
	DetailNotification,
	GetAudience,
	Icons,
	TypographyTypes
} from 'interfaces'
import dayjs from 'lib/dayjs'
import React, { useCallback, useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.css'
import Cookies from 'universal-cookie'
import { CalendarEvent, formatLocalTime } from 'utils'
import { CalendarPanelModal } from './components/CalendarPanelModal'
import { CalendarPanelModalPush } from './components/CalendarPanelModalPush'

export const CalendarPanelPopover: React.FC<{
	event: CalendarEvent
}> = ({ event }) => {
	const [viewMore, setViewMore] = useState(false)
	const [viewMorePush, setViewMorePush] = useState(false)
	const navigate = useNavigate()

	const eventDateTime = dayjs(
		formatLocalTime(event.publish_date, 'YYYY-MM-DDTHH:mm'),
		'YYYY-MM-DDTHH:mm'
	)
	const timeAgo = eventDateTime.fromNow()
	const cookies = new Cookies()
	const userId = cookies.get('userId')
	const organizationId = cookies.get('organization_id')
	const { id } = event
	const { showToast } = useToast()

	const { data, isLoading } = useQuery({
		queryKey: [
			GET_EDIT_CONTENT,
			event.id,
			organizationId,
			userId,
			event.isContent
		],
		queryFn: () => getContentById(organizationId, id),
		refetchOnWindowFocus: false,
		enabled: !!event.isContent
	})

	const { data: dataNotification, isLoading: isLoadingNotification } = useQuery(
		{
			queryKey: [
				GET_DETAIL_NOTIFICATION,
				userId,
				organizationId,
				id,
				event?.isPush
			],
			queryFn: () =>
				getDetailNotification(organizationId, id ? event.id : undefined),
			enabled: !!event?.isPush
		}
	)

	const linkedContentId =
		dataNotification?.cta_type === 'content'
			? Number(dataNotification.cta_value)
			: NaN

	const { data: dataLinkedContent, isLoading: isLoadingLinkedContent } =
		useQuery({
			queryKey: [GET_EDIT_CONTENT, 'linked', organizationId, linkedContentId],
			queryFn: () => getContentById(organizationId, linkedContentId),
			refetchOnWindowFocus: false,
			enabled: event.isPush === true && !Number.isNaN(linkedContentId)
		})

	const { data: dataLocation, isLoading: isLoadingLocation } = useQuery({
		queryKey: [GET_EDIT_CONTENT, organizationId, data],
		queryFn: () =>
			getLocationById(
				organizationId,
				data?.delivery_enhancements?.locations?.[0].location_id
			),
		refetchOnWindowFocus: false,
		enabled: !!data?.delivery_enhancements?.locations?.[0]?.location_id
	})

	const hasVideo =
		(data && data?.basic_setup.preview?.video_link_external) ||
		(data?.basic_setup.preview?.files?.some(
			file => file.asset_type === 'external_video'
		) ??
			false)

	const deliveryEnhancements =
		data?.delivery_enhancements as ContentResponse['delivery_enhancements']

	const hasAudience = deliveryEnhancements?.audience
		? Object.values(deliveryEnhancements.audience).some(
				array => Array.isArray(array) && array.length > 0
		  )
		: false
	const hasAudienceNotification = dataNotification?.audience
		? Object.values(dataNotification.audience).some(
				array => Array.isArray(array) && array.length > 0
		  )
		: false

	const getAudience = (
		dataAudience: DetailNotification | ContentResponse | undefined
	): GetAudience => {
		if (!dataAudience) {
			return {
				tags: [],
				lists: [],
				dynamic_lists: [],
				individuals: [],
				organizations: [],
				feeds: [],
				partner_lists: []
			}
		}

		const audience =
			'audience' in dataAudience
				? dataAudience.audience
				: dataAudience.delivery_enhancements?.audience

		let dynamicLists: number[] = []
		if (audience && 'dynamic_lists' in audience) {
			dynamicLists = audience.dynamic_lists ?? []
		} else if (audience && 'dynamic_list' in audience) {
			dynamicLists = audience.dynamic_list ?? []
		}

		return {
			tags: audience?.tags ?? [],
			lists: audience?.lists ?? [],
			dynamic_lists: dynamicLists,
			individuals: audience?.individuals ?? [],
			organizations: audience?.organizations ?? [],
			feeds: audience?.feeds ?? [],
			partner_lists: audience?.partner_lists ?? []
		}
	}

	const totalList = {
		...getAudience(
			event.isPush
				? (dataNotification as DetailNotification)
				: (data as ContentResponse)
		),
		organization_id: organizationId
	}

	const { data: dataAudience, isLoading: isLoadingAudience } = useQuery({
		queryKey: ['GET_REACH', organizationId, data, totalList],
		queryFn: () => postContentReach(totalList),
		refetchOnWindowFocus: false,
		enabled: Object.values(totalList).some(
			v => Array.isArray(v) && v.length > 0
		)
	})

	const hasTags = (dataAudience?.detailed_reach?.tag?.total ?? 0) > 0
	const hasStaticLists =
		(dataAudience?.detailed_reach?.static_list.total ?? 0) > 0
	const hasDynamicLists =
		(dataAudience?.detailed_reach?.dynamic_list.total ?? 0) > 0
	const hasIndividuals =
		(dataAudience?.detailed_reach?.individual.total ?? 0) > 0

	const handleCopy = useCallback((url: string): void => {
		if (url) {
			navigator.clipboard.writeText(url).then(() => {
				showToast('Success', 'Copied to clipboard.', 'success')
			})
		}
	}, [])

	const hasMultipleAssets = event.assets && event.assets.length > 1
	const pushPublished =
		(event.isPush && event.status === 'scheduled') || event.status === 'draft'

	const copieAgo = event.card_expirated ? 'Expires' : 'Publish'

	const linkedContentImage =
		dataLinkedContent?.basic_setup?.preview?.files?.[0]?.asset_url

	return (
		<>
			<div className="flex flex-col w-full bg-white rounded-lg overflow-y-auto max-h-[30rem] 1lg:max-h-[45rem]">
				<div className="flex w-full justify-between gap-4">
					<div className="flex justify-center items-center border border-1 border-[#C2EDF2] px-4 rounded-md w-1/2 gap-1">
						{event.isContent && (
							<Icon
								fillPath
								src={Icons.speakerNotes}
								className="w-5 h-5 flex-shrink-0 text-blue-1"
							/>
						)}
						{event.isPush && (
							<Icon
								fillPath
								src={Icons.notificationsGeneric}
								className="w-5 h-5 flex-shrink-0 text-blue-1"
							/>
						)}

						<Typography
							title={event.isContent ? 'Content item' : 'Direct Push'}
							type={TypographyTypes.h3}
							fontLeading="14/24"
							className="text-blue-1"
						/>
					</div>
					<div className="flex justify-center items-center border border-1 border-gray-10 px-2 rounded-md gap-2 w-1/2">
						<div className="flex justify-center items-center flex-col">
							<Typography
								title="Publish"
								type={TypographyTypes.span}
								fontLeading="10/17"
								className="text-gray-9"
							/>
							<Typography
								title={dayjs(
									event.card_expirated
										? event.release_date || event.repeat_start_date
										: event.publish_date
								).format('MM/DD/YY')}
								type={TypographyTypes.span}
								fontLeading="14/24"
							/>
						</div>
						{event.expire_date && (
							<>
								<Divider orientation="vertical" variant="middle" flexItem />

								<div className="flex justify-center items-center flex-col">
									<Typography
										title="Expire"
										type={TypographyTypes.span}
										fontLeading="10/17"
										className="text-gray-9"
									/>
									<Typography
										title={dayjs(event.expire_date).format('MM/DD/YY')}
										type={TypographyTypes.span}
										fontLeading="14/24"
									/>
								</div>
							</>
						)}
					</div>
				</div>
				<div className="max-h-[580px] overflow-y-auto mt-2">
					<div className="mt-4 flex flex-row gap-2 items-center">
						<img
							src={event.organization_icon}
							alt={`${event.title}_icon`}
							className="w-[40px] h-[40px] shadow-md rounded-md"
						/>
						<div className="flex flex-col">
							<Typography
								title={event.organization_name}
								type={TypographyTypes.span}
								fontLeading="15/15"
								className="text-gray-9"
							/>
							<Typography
								title={
									timeAgo.includes('in') ? `${copieAgo} ${timeAgo}` : timeAgo
								}
								type={TypographyTypes.span}
								fontLeading="10/17"
								className="text-gray-9"
							/>
						</div>
					</div>

					{event.isPush ? (
						<div className="flex gap-3 items-start my-3">
							{event.assets?.[0] && (
								<img
									src={event.assets[0]}
									alt={event.title}
									className="w-[155px] max-w-[155px] h-auto rounded-lg object-cover flex-shrink-0"
								/>
							)}
							<div className="flex flex-col gap-2 min-w-0">
								<Typography
									title={event.title}
									type={TypographyTypes.span}
									fontLeading="15/15"
									className="text-gray-9 font-bold"
								/>
								<div
									className="text-[15px] text-black-1 font-normal font-Inter line-clamp-4"
									// eslint-disable-next-line react/no-danger
									dangerouslySetInnerHTML={{
										__html: event.description || ''
									}}
								/>
							</div>
						</div>
					) : (
						<>
							<div className="my-3">
								<Swiper
									modules={[Navigation, Pagination]}
									spaceBetween={10}
									slidesPerView={1}
									pagination={{ clickable: true }}
									className="custom-swiper"
									loop={hasMultipleAssets}
									autoplay={
										hasMultipleAssets
											? { delay: 3000, disableOnInteraction: false }
											: false
									}
									allowTouchMove={hasMultipleAssets}
									autoHeight
								>
									{event.assets?.map((src, index) => (
										<SwiperSlide key={index}>
											<img
												src={src}
												alt={`Slide ${index + 1}`}
												className="w-full h-full  rounded-lg"
											/>
										</SwiperSlide>
									))}
								</Swiper>
							</div>
							<div className="flex flex-col gap-2">
								<Typography
									title={event.title}
									type={TypographyTypes.span}
									fontLeading="15/15"
									className="text-gray-9"
								/>
								<div
									className="text-[15px] text-black-1 font-normal font-Inter"
									// eslint-disable-next-line react/no-danger
									dangerouslySetInnerHTML={{
										__html: event.description || ''
									}}
								/>
							</div>
						</>
					)}

					{isLoadingLinkedContent && (
						<div className="mt-3 rounded-lg border border-[#C2EDF2] bg-[#EBF8FA] p-3 flex flex-col gap-2 animate-pulse">
							<div className="h-4 w-36 bg-[#C2EDF2] rounded" />
							<div className="flex gap-3 items-center">
								<div className="w-[60px] h-[60px] rounded-lg bg-[#C2EDF2] flex-shrink-0" />
								<div className="flex flex-col gap-2 flex-1 min-w-0">
									<div className="h-4 w-3/4 bg-[#C2EDF2] rounded" />
									<div className="h-3 w-full bg-[#C2EDF2] rounded" />
								</div>
							</div>
						</div>
					)}

					{dataLinkedContent && (
						<div className="mt-3 rounded-lg border border-[#C2EDF2] bg-[#EBF8FA] p-3 flex flex-col gap-2">
							<div className="flex items-center gap-1.5">
								<Icon
									fillPath
									src={Icons.link}
									className="w-4 h-4 flex-shrink-0 text-blue-1"
								/>
								<Typography
									title="Linked to Content"
									type={TypographyTypes.span}
									fontLeading="13/20"
									className="text-blue-1 font-medium"
								/>
							</div>
							<div className="flex gap-3 items-center">
								{linkedContentImage && (
									<img
										src={linkedContentImage}
										alt={dataLinkedContent.set_up?.title}
										className="w-[60px] h-[60px] rounded-lg object-cover flex-shrink-0"
									/>
								)}
								<div className="flex flex-col gap-0.5 min-w-0">
									<Typography
										title={dataLinkedContent.set_up?.title ?? ''}
										type={TypographyTypes.span}
										fontLeading="14/24"
										className="text-gray-9 font-bold truncate"
									/>
									<Typography
										title={(dataLinkedContent.set_up?.description ?? '')
											.replace(/<[^>]*>/g, '')
											.replace(/&nbsp;/g, ' ')
											.trim()}
										type={TypographyTypes.span}
										fontLeading="15/18"
										className="text-gray-9 !font-normal truncate"
									/>
								</div>
							</div>
						</div>
					)}
				</div>
				<div className="flex w-full justify-between gap-2 mt-2">
					{(event.isContent || pushPublished) && (
						<Button
							label="Edit"
							iconLeft={Icons.edit}
							className="!py-3 !min-w-[45%]"
							color="white"
							onClick={() => {
								if (event.isContent) {
									navigate(`/content/edit/${event.id}`)
									return
								}
								navigate(`/content/push-notification/edit/${event.id}`)
							}}
						/>
					)}

					<Button
						label="View More"
						iconLeft={Icons.plus}
						className={`!py-3 ${
							event.isContent || pushPublished
								? '!min-w-[45%]'
								: '!min-w-[100%]'
						}`}
						onClick={() =>
							event.isContent ? setViewMore(true) : setViewMorePush(true)
						}
					/>
				</div>
			</div>

			<CalendarPanelModal
				viewMore={viewMore}
				setViewMore={setViewMore}
				data={data as ContentResponse}
				dataLocation={dataLocation}
				dataAudience={dataAudience}
				isLoading={isLoading}
				isLoadingLocation={isLoadingLocation}
				isLoadingAudience={isLoadingAudience}
				organizationId={organizationId}
				hasVideo={hasVideo as boolean}
				deliveryEnhancements={deliveryEnhancements}
				hasAudience={hasAudience}
				hasTags={hasTags}
				hasStaticLists={hasStaticLists}
				hasDynamicLists={hasDynamicLists}
				hasIndividuals={hasIndividuals}
				handleCopy={handleCopy}
				id={event.id}
			/>
			<CalendarPanelModalPush
				handleCopy={handleCopy}
				viewMore={viewMorePush}
				setViewMore={setViewMorePush}
				data={dataNotification as DetailNotification}
				dataAudience={dataAudience}
				isLoading={isLoadingNotification}
				isLoadingLocation={isLoadingLocation}
				isLoadingAudience={isLoadingAudience}
				organizationId={organizationId}
				hasAudience={hasAudienceNotification}
				hasTags={hasTags}
				hasStaticLists={hasStaticLists}
				hasDynamicLists={hasDynamicLists}
				hasIndividuals={hasIndividuals}
				id={event.id}
				isEdit={pushPublished}
			/>
		</>
	)
}
